/* Human-readable labels for pieces and events (logs, tests, the sim). */
import { SHAPES } from './shapes.ts';
import { cellName, cellNameOf } from './position.ts';
import type { Position } from './position.ts';
import { BLACK } from './types.ts';
import type { Event } from './types.ts';

const ARROW: Record<string, string> = { '1,0': 'N', '1,1': 'NE', '0,1': 'E', '-1,1': 'SE', '-1,0': 'S', '-1,-1': 'SW', '0,-1': 'W', '1,-1': 'NW', '0,0': '-' };
export const dirName = (dr: number, dc: number): string => ARROW[`${dr},${dc}`] ?? '?';

/** "White triangle E5" (position after the event; lost pieces have no cell). */
export function pieceLabel(p: Position, i: number): string {
  const side = p.owner[i] === BLACK ? 'Black' : 'White';
  const where = p.alive[i] ? ' ' + cellName(p.row[i], p.col[i]) : '';
  return `${side} ${SHAPES[p.shape[i]].name}${where}`;
}

/** One line per event. `before` is the position the move was applied to (for the mover's origin), `after` the result. */
export function describeEvents(before: Position, after: Position, events: Event[]): string[] {
  const name = (i: number): string => `${after.owner[i] === BLACK ? 'Black' : 'White'} ${SHAPES[after.shape[i]].name}`;
  const out: string[] = [];
  for (const e of events) {
    switch (e.kind) {
      case 'move': out.push(`${name(e.piece)} ${cellNameOf(e.from)} -> ${cellNameOf(e.to)}${e.solo ? ' (pulled free)' : e.body.length > 1 ? ` (body of ${e.body.length})` : ''}`); break;
      case 'unbond': out.push(`  bonds broken: ${e.partners.map(j => name(j)).join(', ')}`); break;
      case 'drag-off': out.push(`  ${name(e.piece)} dragged off the board`); break;
      case 'wave': out.push(`  wave ${e.wave}`); break;
      case 'cancel': out.push(`    forces on ${e.body.map(name).join('+')} cancel`); break;
      case 'push': out.push(`    ${e.body.map((m, k) => `${name(m)} ${e.from[k] >= 0 ? cellNameOf(e.from[k]) : '?'}`).join(' + ')} pushed ${dirName(e.dr, e.dc)}`); break;
      case 'pushed-off': out.push(`    ${name(e.piece)} pushed off the board`); break;
      case 'blocked': out.push(`    ${e.body.map(m => name(m) + (after.alive[m] ? ' ' + cellName(after.row[m], after.col[m]) : '')).join('+')} ${e.reason}${e.reason === 'jammed' ? '' : ' going ' + dirName(e.dr, e.dc)}`); break;
      case 'bond': out.push(`    ${name(e.a)} ${cellName(after.row[e.a], after.col[e.a])} bonds with ${name(e.b)} ${cellName(after.row[e.b], after.col[e.b])}`); break;
      case 'cutoff': out.push(`    resolution cut off after ${e.wave} waves`); break;
      case 'contact': break;   // presentation detail; the push/bond lines say what came of it
    }
  }
  void before;
  return out;
}
